import { useState, useEffect, useRef, useCallback } from 'react';
import { X, MapPin, Navigation, Search, CheckCircle } from 'lucide-react';

const DEFAULT = { lat: 17.2, lng: -62.65 };

// St. Kitts & Nevis sit at ~17°N, 62°W — a positive longitude inside this
// latitude band is a dropped sign (+62.84 → -62.84). Normalize it back so
// the map, geocode, and saved GeoPoint always agree.
export function normalizeLng(lat, lng) {
  if (lat >= 16.8 && lat <= 17.6 && lng > 62.3 && lng < 63.2) return -lng;
  return lng;
}

// Load Leaflet (OpenStreetMap)
const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      console.log('Leaflet loaded successfully');
      resolve(window.L);
    };
    script.onerror = () => {
      reject(new Error('Failed to load Leaflet'));
    };
    document.head.appendChild(script);
  });
};

function detectIsland(lat, lng) {
  if (lat >= 17.09 && lat <= 17.22 && lng >= -62.65 && lng <= -62.54) return 'Nevis';
  return 'Saint Kitts';
}

function detectParish(lat, lng) {
  if (lng < -62.76) return 'Saint Thomas Middle Island';
  if (lng < -62.71) return 'Saint George Basseterre';
  if (lng < -62.67) return 'Saint Peter Basseterre';
  return 'Saint Paul Capisterre';
}

export default function LocationPicker({ onLocationSelect, onClose, initialCoordinates }) {
  const initCoords = (() => {
    const src = initialCoordinates || DEFAULT;
    const lat = src.lat ?? src.latitude ?? DEFAULT.lat;
    const lng = normalizeLng(lat, src.lng ?? src.longitude ?? DEFAULT.lng);
    return { lat, lng };
  })();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocodeTimer = useRef(null);

  const [address, setAddress] = useState('Pan the map to position the pin');
  const [coords, setCoords] = useState(initCoords);
  const [geocoding, setGeocoding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);

  const reverseGeocode = useCallback(async (lat, lng) => {
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
        { headers: { 'User-Agent': 'ChefSKN/1.0', 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      if (data?.display_name) {
        setAddress(data.display_name.split(',').slice(0, 3).join(', '));
      } else {
        setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      }
    } catch {
      setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } finally {
      setGeocoding(false);
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    let cleanup = null;

    const initLeafletMap = () => {
      if (!window.L) return;
      const map = window.L.map(mapRef.current).setView([initCoords.lat, initCoords.lng], 10);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);
      mapInstanceRef.current = map;
      map.on('moveend', () => {
        const center = map.getCenter();
        const lat = center.lat;
        const lng = normalizeLng(lat, center.lng);
        setCoords({ lat, lng });
        if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
        geocodeTimer.current = setTimeout(() => reverseGeocode(lat, lng), 700);
      });
      cleanup = () => map.remove();
    };

    // Load Leaflet and initialize map
    loadLeaflet()
      .then(() => {
        initLeafletMap();
        setMapLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load Leaflet:', err);
        setMapLoading(false);
      });

    return () => { if (cleanup) cleanup(); };
  }, [reverseGeocode]);

  const panTo = (lat, lng, zoom = 17) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], zoom);
    } else {
      const nLng = normalizeLng(lat, lng);
      setCoords({ lat, lng: nLng });
      reverseGeocode(lat, nLng);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => panTo(pos.coords.latitude, pos.coords.longitude, 17),
      () => setAddress('Could not get location — search manually.')
    );
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery + ', Saint Kitts Nevis')}&limit=1`,
        { headers: { 'User-Agent': 'ChefSKN/1.0' } }
      );
      const data = await res.json();
      if (data?.[0]) {
        const { lat, lon, display_name } = data[0];
        setAddress(display_name.split(',').slice(0, 3).join(', '));
        panTo(parseFloat(lat), parseFloat(lon), 17);
      } else {
        setAddress('Location not found — try a different search');
      }
    } catch {
      setAddress('Search failed. Try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (!coords?.lat) return;
    onLocationSelect({
      coordinates: { latitude: coords.lat, longitude: coords.lng },
      street: address,
      parish: detectParish(coords.lat, coords.lng),
      island: detectIsland(coords.lat, coords.lng),
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', backgroundColor: '#F2F2F7' }}>
      {/* iOS nav bar — X close, centered "Set Location" title */}
      <div style={{ backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', position: 'relative' }}>
        <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} aria-label="Close">
          <X size={16} color="#6D6D72" />
        </button>
        <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', color: '#111827', fontWeight: 600, fontSize: 17 }}>Set Location</span>
        <span style={{ flex: 1 }} />
      </div>

      {/* Search — iOS gray rounded field */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, padding: '10px 12px', backgroundColor: '#F2F2F7', flexShrink: 0 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', backgroundColor: '#E5E5EA', borderRadius: 10, padding: '0 12px', gap: 8 }}>
          <Search size={16} color="#8E8E93" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search street, area or landmark…" style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 15, padding: '8px 0', color: '#111827' }} />
        </div>
        <button type="submit" disabled={searching || !searchQuery.trim()} style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, padding: '0 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer', opacity: searching || !searchQuery.trim() ? 0.5 : 1 }}>
          {searching ? '…' : 'Go'}
        </button>
      </form>

      {/* Map with fixed centered pin */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: '300px', backgroundColor: '#e5e7eb' }}>
        {mapLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e5e7eb', zIndex: 5 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, border: '4px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
              <p style={{ color: '#6b7280', fontSize: 14 }}>Loading map...</p>
            </div>
          </div>
        )}
        <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: '300px' }} />

        {/* iOS location.circle.fill — current location button on map, bottom-right */}
        <button
          onClick={handleUseCurrentLocation}
          title="Use my current location"
          style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 1000, width: 44, height: 44, borderRadius: '50%', backgroundColor: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
        >
          <Navigation size={22} color="#FF3B30" />
        </button>

        {/* Fixed centered crosshair pin — map pans under it */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 1000, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.45))' }}>
          <svg width="38" height="52" viewBox="0 0 38 52" fill="none">
            <path d="M19 0C10.163 0 3 7.163 3 16C3 28 19 52 19 52C19 52 35 28 35 16C35 7.163 27.837 0 19 0Z" fill="#dc2626"/>
            <circle cx="19" cy="16" r="8" fill="white"/>
            <circle cx="19" cy="16" r="5" fill="#dc2626"/>
          </svg>
        </div>

        {/* Pin shadow */}
        <div style={{ position: 'absolute', top: 'calc(50% + 3px)', left: '50%', transform: 'translate(-50%, -50%)', width: 14, height: 7, background: 'rgba(0,0,0,0.22)', borderRadius: '50%', filter: 'blur(3px)', pointerEvents: 'none', zIndex: 999 }} />

        {/* Instruction chip */}
        <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600, pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 11 }}>
          Pan map · pin stays centered
        </div>
      </div>

      {/* Bottom: address + confirm */}
      <div style={{ flexShrink: 0, backgroundColor: '#fff', borderTop: '1px solid #e5e7eb', padding: '14px 16px 24px', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <MapPin size={20} color="#dc2626" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Selected location</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.4 }}>
              {geocoding ? 'Reading address…' : address}
            </p>
            {coords && (
              <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9ca3af' }}>
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
              </p>
            )}
          </div>
        </div>
        <button onClick={handleConfirm} style={{ width: '100%', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: 12, padding: 14, fontWeight: 700, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <CheckCircle size={20} />
          Confirm Location
        </button>
      </div>
    </div>
  );
}
